import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Moon, Sun, Bell, Megaphone, MessageCircle, LayoutGrid, User, LogOut, Settings, ShoppingCart, Contact, Package, Wallet, LineChart, Warehouse, ClipboardList, Banknote } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { TIP_ETIKET } from "@/components/erp";
import { can } from "@/lib/perms";
import { modKaydet, useMod } from "@/lib/mod";
import api from "@/lib/api";

const UYGULAMALAR = [
  { to: "/siparisler", label: "Siparişler", icon: ShoppingCart, perm: "siparis:read" }, { to: "/cariler", label: "Cariler", icon: Contact, perm: "cari:read" },
  { to: "/urunler", label: "Ürünler", icon: Package }, { to: "/tahsilatlar", label: "Tahsilatlar", icon: Wallet, perm: "tahsilat:read" },
  { to: "/tedarikci-odemeleri", label: "Ödemeler", icon: Banknote, perm: "odeme:read" }, { to: "/nakit-akis", label: "Nakit Akışı", icon: LineChart, perm: "odeme:read" },
  { to: "/depo", label: "Depo", icon: Warehouse, perm: "stok:read" }, { to: "/uretim", label: "Üretim", icon: ClipboardList, perm: "uretim:read" },
];
const ikonCls = "relative w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/10 transition-colors";

const ModDugme = () => {
  const gunduz = useMod() === "gunduz";
  return (
    <button data-testid="tema-mod-btn" title={gunduz ? "Gece moduna geç" : "Gündüz moduna geç"} className={ikonCls}
      onClick={() => modKaydet(gunduz ? "gece" : "gunduz")}>
      {gunduz ? <Moon className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
    </button>
  );
};

const Bildirim = ({ ozet }) => {
  const { pathname } = useLocation();
  const [yeniOzet, setYeniOzet] = useState(false);
  const [b, setB] = useState({ liste: [], okunmamis: 0 });
  const [acik, setAcik] = useState(false);
  useEffect(() => {
    const bugun = new Date().toISOString().slice(0, 10);
    api.get("/uygulama-bildirimleri").then((r) => setB(r.data)).catch(() => {});
    if (ozet) Promise.all([api.get("/bildirim/gunluk-ozet").catch(() => ({ data: null })), api.get("/hatirlatmalar").catch(() => ({ data: [] }))])
      .then(([o, h]) => setYeniOzet((!!o.data && !o.data.okundu) || h.data.some((n) => n.hatirlatma <= bugun)));
  }, [pathname, ozet]);
  const ac = (v) => { setAcik(v); if (v && b.okunmamis) { api.post("/uygulama-bildirimleri/okundu").then(() => setB((s) => ({ ...s, okunmamis: 0 }))); } };
  return (
    <Popover open={acik} onOpenChange={ac}>
      <PopoverTrigger asChild>
        <button data-testid="ust-bildirim" title="Bildirimler" className={ikonCls}>
          <Bell className="w-[18px] h-[18px]" />{(b.okunmamis > 0 || (yeniOzet && pathname !== "/gunluk-ozet")) && <span data-testid="ust-bildirim-nokta" className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-400" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="glass w-[360px] p-0" data-testid="bildirim-panel">
        <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold">Bildirimler</div>
        <div className="max-h-[360px] overflow-y-auto erp-scroll">
          {b.liste.length === 0 && <div className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="bildirim-bos">Yeni bildirim yok.</div>}
          {b.liste.map((n) => (
            <Link key={n.id} to={n.link} onClick={() => setAcik(false)} data-testid={`bildirim-${n.id}`} className={`block px-4 py-3 border-b border-white/5 hover:bg-white/5 ${n.okundu ? "opacity-70" : ""}`}>
              <div className="text-xs font-semibold">{n.baslik}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{n.mesaj}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
            </Link>))}
        </div>
        {ozet && <Link to="/gunluk-ozet" onClick={() => setAcik(false)} data-testid="bildirim-gunluk-ozet" className="flex items-center justify-between px-4 py-3 text-xs hover:bg-white/5">Günlük özet{yeniOzet && <span className="w-2 h-2 rounded-full bg-amber-400" />}</Link>}
      </PopoverContent>
    </Popover>
  );
};

const useAcik = () => {
  const { pathname } = useLocation();
  const [acik, setAcik] = useState(false);
  useEffect(() => { setAcik(false); }, [pathname]);
  return [acik, setAcik];
};

const Uygulamalar = ({ user }) => {
  const [acik, setAcik] = useAcik();
  return (
  <Popover open={acik} onOpenChange={setAcik}>
    <PopoverTrigger asChild><button data-testid="ust-uygulamalar" title="Hızlı erişim" className={ikonCls}><LayoutGrid className="w-[18px] h-[18px]" /></button></PopoverTrigger>
    <PopoverContent align="end" className="w-72 p-3 glass" data-testid="ust-uygulamalar-panel">
      <div className="grid grid-cols-3 gap-1.5">{UYGULAMALAR.filter((u) => !u.perm || can(user, u.perm)).map((u) => (
        <Link key={u.to} to={u.to} data-testid={`ust-uyg-${u.to.slice(1)}`} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl text-[11px] text-center hover:bg-white/10 transition-colors">
          <span className="w-9 h-9 rounded-xl grad-bg text-white flex items-center justify-center"><u.icon className="w-4 h-4" /></span>{u.label}
        </Link>
      ))}</div>
    </PopoverContent>
  </Popover>
  );
};

const Profil = ({ user, logout }) => {
  const [acik, setAcik] = useAcik();
  return (
  <Popover open={acik} onOpenChange={setAcik}>
    <PopoverTrigger asChild><button data-testid="ust-profil" title="Profil" className={ikonCls}><User className="w-[18px] h-[18px]" /></button></PopoverTrigger>
    <PopoverContent align="end" className="w-60 p-2 glass" data-testid="ust-profil-panel">
      <div className="px-3 py-2 border-b border-white/10 mb-1">
        <div className="text-sm font-semibold truncate">{user?.name}</div>
        <div className="text-xs text-muted-foreground truncate">{user?.email} · {TIP_ETIKET[user?.role] || user?.role}</div>
      </div>
      {user?.role === "admin" && <Link to="/ayarlar" data-testid="ust-profil-ayarlar" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-white/10"><Settings className="w-4 h-4" />Ayarlar</Link>}
      <button data-testid="ust-profil-cikis" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:bg-white/10"><LogOut className="w-4 h-4" />Çıkış yap</button>
    </PopoverContent>
  </Popover>
  );
};

export const UstMenu = () => {
  const { user, logout } = useAuth();
  return (
    <div className="flex items-center gap-0.5" data-testid="ust-menu">
      <Link to="/yardim" data-testid="ust-destek" title="Destek / Yardım" className={`${ikonCls} hidden sm:flex`}><MessageCircle className="w-[18px] h-[18px]" /></Link>
      {can(user, "cari:write") && <Link to="/bayi-duyurulari" data-testid="ust-duyurular" title="Duyurular" className={`${ikonCls} hidden sm:flex`}><Megaphone className="w-[18px] h-[18px]" /></Link>}
      <Bildirim ozet={can(user, "ozet:read")} />
      <ModDugme />
      <Uygulamalar user={user} />
      <Profil user={user} logout={logout} />
    </div>
  );
};
